'use client';

import React from 'react';
import { MessageSquare, Image as ImageIcon, CheckCircle, Shield, AlertTriangle, ExternalLink } from 'lucide-react';
import Link from 'next/link';
import { formatPhoneNumber } from '@voley/shared-utils';
import type { PhoneNumberViewModel, CampaignViewModel } from '@/features/settings/types';

interface PhoneNumberCardProps {
  phoneNumber: PhoneNumberViewModel;
  campaign?: CampaignViewModel | null;
}

const PhoneNumberCard: React.FC<PhoneNumberCardProps> = ({
  phoneNumber,
  campaign
}) => {
  const isApproved = campaign?.status === 'approved' || campaign?.status === 'connected';
  const hasCampaign = !!campaign;

  return (
    <div className="bg-card-background rounded-lg border border-border p-5 space-y-4">
      {/* Top row: phone number + status */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold text-text-primary">{formatPhoneNumber(phoneNumber.phoneNumber || phoneNumber.formattedNumber)}</h2>
            <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
              phoneNumber.status === 'active'
                ? 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30'
                : phoneNumber.status === 'provisioned'
                ? 'text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30'
                : 'text-gray-700 dark:text-gray-400 bg-gray-100 dark:bg-gray-800'
            }`}>
              {phoneNumber.status.charAt(0).toUpperCase() + phoneNumber.status.slice(1)}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            {phoneNumber.capabilities.sms && (
              <span className="text-xs text-text-secondary flex items-center">
                <MessageSquare className="h-3 w-3 mr-1" />
                SMS
              </span>
            )}
            {phoneNumber.capabilities.mms && (
              <span className="text-xs text-text-secondary flex items-center">
                <ImageIcon className="h-3 w-3 mr-1" />
                MMS
              </span>
            )}
            <span className="text-xs text-text-secondary">
              · Since {phoneNumber.dateAcquired ? new Date(phoneNumber.dateAcquired).toLocaleDateString() : 'N/A'}
            </span>
          </div>
        </div>

        {/* 10DLC status badge */}
        {isApproved ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <div>
              <div className="text-sm font-semibold text-green-700 dark:text-green-400">Approved</div>
              <div className="text-xs text-green-600 dark:text-green-500">10DLC</div>
            </div>
          </div>
        ) : hasCampaign ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <Shield className="h-5 w-5 text-amber-500" />
            <div>
              <div className="text-sm font-semibold text-amber-700 dark:text-amber-400">Pending</div>
              <div className="text-xs text-amber-600 dark:text-amber-500">10DLC</div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <AlertTriangle className="h-5 w-5 text-gray-400" />
            <div>
              <div className="text-sm font-semibold text-gray-600 dark:text-gray-400">Not Registered</div>
              <div className="text-xs text-gray-500 dark:text-gray-500">10DLC</div>
            </div>
          </div>
        )}
      </div>

      {/* 10DLC Campaign Details */}
      {hasCampaign && (isApproved || campaign.brandName) ? (
        <div className="border-t border-border pt-4">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="h-4 w-4 text-text-secondary" />
            <h3 className="text-sm font-medium text-text-primary">10DLC Campaign</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {campaign.brandName && (
              <div className="p-2.5 rounded-lg bg-hover-background">
                <div className="text-xs uppercase tracking-wider text-text-secondary mb-0.5">Brand</div>
                <div className="text-sm font-medium text-text-primary truncate">{campaign.brandName}</div>
              </div>
            )}
            {campaign.useCase && (
              <div className="p-2.5 rounded-lg bg-hover-background">
                <div className="text-xs uppercase tracking-wider text-text-secondary mb-0.5">Use Case</div>
                <div className="text-sm font-medium text-text-primary truncate">{campaign.useCase}</div>
              </div>
            )}
            {campaign.approvedDate && (
              <div className="p-2.5 rounded-lg bg-hover-background">
                <div className="text-xs uppercase tracking-wider text-text-secondary mb-0.5">Approved</div>
                <div className="text-sm font-medium text-text-primary">
                  {new Date(campaign.approvedDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </div>
              </div>
            )}
            {campaign.throughput != null && campaign.throughput > 0 && (
              <div className="p-2.5 rounded-lg bg-hover-background">
                <div className="text-xs uppercase tracking-wider text-text-secondary mb-0.5">Throughput</div>
                <div className="text-sm font-medium text-text-primary">{campaign.throughput} msg/s</div>
              </div>
            )}
          </div>
        </div>
      ) : !hasCampaign ? (
        <div className="border-t border-border pt-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-800/30">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-text-primary">10DLC registration required for A2P messaging compliance.</p>
              <p className="text-xs text-text-secondary mt-0.5">Register your business to improve deliverability and avoid carrier filtering.</p>
            </div>
            <Link
              href="/10dlc"
              className="inline-flex items-center text-xs font-medium text-primary hover:underline shrink-0"
            >
              Register
              <ExternalLink className="h-3 w-3 ml-1" />
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PhoneNumberCard;
